/**
 * RSS 2.0 / Atom / RDF parsing (sources.md "rss" Parse), shared by rss, youtube, reddit and discourse.
 * `fast-xml-parser` with namespace prefixes removed, reusing the v4 mapping:
 *   entries   rss.channel.item | feed.entry | RDF.item
 *   link      string, or Atom link[rel=alternate]
 *   body      content:encoded → Atom content → description → summary
 *   date      pubDate → published → updated → dc:date
 *   author    dc:creator → author.name → author
 *   image     media:thumbnail → media:content[medium=image] → image enclosure
 *   id        guid → Atom id → link → rdf:about → SHA-256 of title + date
 */

import { XMLParser } from 'fast-xml-parser';
import type { EpochMs } from '../contracts/ids.js';
import type { RawItem } from '../contracts/sources.js';
import { DigesterError } from '../kernel/errors.js';
import { htmlToText } from '../kernel/html.js';
import { sha256Hex } from '../kernel/ids.js';
import { absoluteUrl, parseDate } from './http.js';

// parseTagValue / parseAttributeValue off keep guids, dates and numeric-looking ids as strings.
const parser = new XMLParser({
  ignoreAttributes: false,
  attributeNamePrefix: '@_',
  removeNSPrefix: true,
  parseTagValue: false,
  parseAttributeValue: false,
  trimValues: true,
  textNodeName: '#text',
  processEntities: true,
  // XML entities only: HTML bodies keep their own escaping (a second decode could turn &lt; into markup).
  htmlEntities: false,
});

export type XmlNode = Record<string, unknown>;

export function isRecord(v: unknown): v is XmlNode {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function toArray(v: unknown): unknown[] {
  if (v === undefined || v === null) return [];
  return Array.isArray(v) ? v : [v];
}

/** A field's text: a string, a `{ '#text' }` node (when it carries attributes), or ''. */
export function textOf(v: unknown): string {
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (isRecord(v) && (typeof v['#text'] === 'string' || typeof v['#text'] === 'number')) return String(v['#text']).trim();
  if (Array.isArray(v)) {
    for (const x of v) {
      const t = textOf(x);
      if (t) return t;
    }
  }
  return '';
}

export function attr(node: unknown, name: string): string | null {
  if (!isRecord(node)) return null;
  const v = node[`@_${name}`];
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

export interface ParsedFeed {
  dialect: 'rss' | 'atom' | 'rdf';
  title: string;
  /** The site link of the feed, if any. */
  link: string | null;
  entries: XmlNode[];
}

/** Parse a feed document. Throws parse_failed when it is not RSS, Atom or RDF. */
export function parseFeed(xml: string, what: string): ParsedFeed {
  let doc: unknown;
  try {
    doc = parser.parse(xml);
  } catch {
    throw new DigesterError('parse_failed', `${what} is not valid XML.`);
  }
  if (!isRecord(doc)) throw new DigesterError('parse_failed', `${what} is empty.`);
  if (isRecord(doc.rss) && isRecord(doc.rss.channel)) {
    const channel = doc.rss.channel;
    return { dialect: 'rss', title: htmlToText(textOf(channel.title)), link: linkOf(channel), entries: toArray(channel.item).filter(isRecord) };
  }
  if (isRecord(doc.RDF)) {
    const channel = isRecord(doc.RDF.channel) ? doc.RDF.channel : {};
    return { dialect: 'rdf', title: htmlToText(textOf(channel.title)), link: linkOf(channel), entries: toArray(doc.RDF.item).filter(isRecord) };
  }
  if (isRecord(doc.feed)) {
    return { dialect: 'atom', title: htmlToText(textOf(doc.feed.title)), link: linkOf(doc.feed), entries: toArray(doc.feed.entry).filter(isRecord) };
  }
  throw new DigesterError('parse_failed', `${what} is not an RSS, Atom or RDF feed.`);
}

/** RSS/RDF give a string; Atom gives `{ @_href, @_rel }` objects (rel=alternate, or none, wins). */
export function linkOf(node: XmlNode): string | null {
  const raw = node.link;
  if (typeof raw === 'string') return raw.trim() || null;
  const links = toArray(raw);
  const strings = links.filter((l): l is string => typeof l === 'string' && l.trim() !== '');
  if (strings.length > 0) return (strings[0] as string).trim();
  const objects = links.filter(isRecord);
  if (objects.length === 0) return null;
  const alt = objects.find((l) => attr(l, 'rel') === 'alternate' || attr(l, 'rel') === null);
  return attr(alt ?? objects[0], 'href') ?? (textOf(alt ?? objects[0]) || null);
}

/** Atom `content` and MRSS `media:content` collapse to the same key; the media ones carry @_url. */
function contentNodes(entry: XmlNode): { text: string; media: XmlNode[] } {
  let text = '';
  const media: XmlNode[] = [];
  for (const c of toArray(entry.content)) {
    if (isRecord(c) && attr(c, 'url') !== null) media.push(c);
    else if (!text) text = textOf(c);
  }
  return { text, media };
}

function imageOf(entry: XmlNode, base: string | null): string | null {
  const groups = [entry, ...toArray(entry.group).filter(isRecord)];
  for (const g of groups) {
    for (const t of toArray(g.thumbnail)) {
      const u = absoluteUrl(attr(t, 'url'), base);
      if (u) return u;
    }
  }
  for (const g of groups) {
    for (const m of contentNodes(g).media) {
      const medium = attr(m, 'medium');
      const type = attr(m, 'type') ?? '';
      if (medium === 'image' || type.startsWith('image/')) {
        const u = absoluteUrl(attr(m, 'url'), base);
        if (u) return u;
      }
    }
  }
  for (const e of toArray(entry.enclosure)) {
    if ((attr(e, 'type') ?? '').startsWith('image/')) {
      const u = absoluteUrl(attr(e, 'url'), base);
      if (u) return u;
    }
  }
  return null;
}

function authorOf(entry: XmlNode): string | undefined {
  const creator = textOf(entry.creator);
  if (creator) return htmlToText(creator);
  for (const a of toArray(entry.author)) {
    if (isRecord(a) && textOf(a.name)) return htmlToText(textOf(a.name));
    const t = textOf(a);
    if (t) return htmlToText(t);
  }
  return undefined;
}

export function entryBody(entry: XmlNode): string {
  return textOf(entry.encoded) || contentNodes(entry).text || textOf(entry.description) || textOf(entry.summary);
}

export function entryDate(entry: XmlNode): EpochMs | undefined {
  return parseDate(textOf(entry.pubDate) || textOf(entry.published) || textOf(entry.updated) || textOf(entry.date));
}

/** The v4 mapping of one entry to a RawItem; `base` resolves relative links (the feed URL). */
export function entryToItem(entry: XmlNode, base: string | null): RawItem | null {
  const url = absoluteUrl(linkOf(entry), base) ?? undefined;
  const title = htmlToText(textOf(entry.title)) || undefined;
  const publishedAt = entryDate(entry);
  const guid = textOf(entry.guid) || textOf(entry.id) || url || attr(entry, 'about');
  const externalId = guid || (title || publishedAt !== undefined ? sha256Hex(`${title ?? ''}\n${publishedAt ?? ''}`) : null);
  if (!externalId) return null;
  const item: RawItem = { externalId };
  if (url) item.url = url;
  if (title) item.title = title;
  if (publishedAt !== undefined) item.publishedAt = publishedAt;
  const author = authorOf(entry);
  if (author) item.author = author;
  const body = entryBody(entry);
  if (body) item.html = body;
  const image = imageOf(entry, url ?? base);
  if (image) item.imageUrl = image;
  return item;
}

/** Autodiscovery: the first `<link rel="alternate" type="application/rss+xml|atom+xml">` of an HTML page. */
export function discoverFeedUrl(html: string, pageUrl: string): string | null {
  for (const m of html.matchAll(/<link\b[^>]*>/gi)) {
    const tag = m[0];
    const rel = /\brel\s*=\s*["']?([^"'\s>]+)/i.exec(tag)?.[1]?.toLowerCase();
    const type = /\btype\s*=\s*["']?([^"'\s>]+)/i.exec(tag)?.[1]?.toLowerCase();
    const href = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(tag);
    if (rel !== 'alternate' || (type !== 'application/rss+xml' && type !== 'application/atom+xml') || !href) continue;
    const value = decodeAttr(href[1] ?? href[2] ?? href[3] ?? '');
    const abs = absoluteUrl(value, pageUrl);
    if (abs) return abs;
  }
  return null;
}

function decodeAttr(s: string): string {
  return s.replace(/&amp;/g, '&').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&lt;/g, '<').replace(/&gt;/g, '>');
}

/** Does this payload look like a feed (rather than an HTML page)? */
export function looksLikeFeed(text: string, contentType: string | undefined): boolean {
  const ct = (contentType ?? '').toLowerCase();
  if (ct.includes('rss') || ct.includes('atom') || ct.includes('rdf')) return true;
  const head = text.slice(0, 2048).trimStart();
  if (/^<!doctype html|^<html/i.test(head)) return false;
  return /<(rss|feed|rdf:RDF)[\s>]/i.test(head);
}
